import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { BcmDynamicPriceDto } from '@shared/models/bcm-dynamic-price';
import { DefaultBerthReservationTimeUnit } from '@shared/models/bcm-settings';
import { DurationUnit, getDurationFromDates } from '@shared/functions/get-duration-from-dates';

// Duration / traveler inputs the rule engine multiplies factors by. Only > 0 values
// are carried (the engine gates on `> 0`).
export interface DynamicPriceDurations {
    days?: number;
    overnights?: number;
    travelers?: number;
}

// Minimal evaluator surface (the concrete ProductsApiService satisfies it) so this
// helper stays Angular-free and unit-testable in isolation.
export interface DynamicPriceEvaluator {
    evaluatePriceRule(
        productId: number,
        parameters: Record<string, { id?: number } | null | undefined>,
        quantity: number,
        boatDimensions?: { width: number; length: number },
        durations?: DynamicPriceDurations,
    ): Observable<Partial<BcmDynamicPriceDto>>;
}

// Derive the engine's duration/traveler inputs from a vesting period + traveler
// count. Days needs no settings; Overnights needs the tenant's guest-hours config
// (skipped if it has not loaded yet — Days still flows). Mirrors the legacy
// getPositionWithDynamicPrice duration math.
export function durationsOf(
    from: Date | null,
    to: Date | null,
    travelers: number,
    timeUnit: DefaultBerthReservationTimeUnit | null | undefined,
): DynamicPriceDurations {
    const out: DynamicPriceDurations = {};
    if (from && to) {
        const days = getDurationFromDates(from, to, DurationUnit.Days, timeUnit as DefaultBerthReservationTimeUnit);
        if (days != null && days > 0) {
            out.days = days;
        }
        if (timeUnit) {
            const overnights = getDurationFromDates(from, to, DurationUnit.Overnights, timeUnit);
            if (overnights != null && overnights > 0) {
                out.overnights = overnights;
            }
        }
    }
    if (travelers > 0) {
        out.travelers = travelers;
    }
    return out;
}

// One product's evaluated price tagged with a caller-chosen key (productId for the
// relation flow, the service-line id for the services flow). `null` = the request
// failed → keep the existing price.
export type EvaluatedPrice<K = number | string> = {
    key: K;
    price: number | null;
    ruleName: string | null;
    dynamicPrice: Partial<BcmDynamicPriceDto> | null;
} | null;

// The dimensions object the rule engine expects, or undefined when either side is
// unknown (a manual boat with no measurements).
export function boatDimensionsOf(
    length: number | null,
    width: number | null,
): { length: number; width: number } | undefined {
    return length != null && width != null ? { length, width } : undefined;
}

// Evaluate one product's dynamic price and map it to a keyed EvaluatedPrice. A
// failed request resolves to `null` (caught) so a forkJoin over many products is
// never torn down by a single failure.
export function evaluateProductPrice<K>(
    api: DynamicPriceEvaluator,
    input: {
        key: K;
        productId: number;
        quantity: number;
        parameters: Record<string, { id?: number } | null | undefined>;
        boatDimensions?: { width: number; length: number };
        durations?: DynamicPriceDurations;
    },
): Observable<EvaluatedPrice<K>> {
    return api
        .evaluatePriceRule(input.productId, input.parameters, input.quantity, input.boatDimensions, input.durations)
        .pipe(
            map((dp): EvaluatedPrice<K> => ({
                key: input.key,
                price: dp?.rulePrice ?? null,
                ruleName: dp?.ruleName ?? null,
                dynamicPrice: dp ?? null,
            })),
            catchError(() => of<EvaluatedPrice<K>>(null)),
        );
}
